import type { AxesNode } from '@/lib/threeScene/nodes/axes';
import type { GridNode } from '@/lib/threeScene/nodes/grid';
import type { GroupNode } from '@/lib/threeScene/nodes/group';
import type { AxisNode } from '@/lib/threeScene/nodes/axis';
import { SceneContext, type SceneState } from '@/lib/threeScene/SceneContext';
import { DirectionalLight, HemisphereLight, Scene } from 'three';
import { addObject } from '@/lib/threeScene/object3d';
import { lpsPoint } from '@/domain/anatomy/LPS';
import type { OverlayMeshNode } from '@/views/dicoms/overlay/scene/overlayMesh';
import { addOverlayMaterials } from '@/views/dicoms/overlay/scene/overlayMaterials';
import type { ImagePlaneNode } from '@/lib/threeScene/nodes/imagePlane';

export type CatstackOverlayNode =
    | AxesNode
    | GridNode
    | GroupNode
    | AxisNode
    | OverlayMeshNode
    | ImagePlaneNode;

export class OverlaySceneContext extends SceneContext<CatstackOverlayNode> {
}

export function catstackOverlaySceneContext(state: SceneState<CatstackOverlayNode>): OverlaySceneContext {
    const scene = new Scene();
    scene.up.set(0, 0, 1);
    addStandardLights(scene);

    const sceneContext = new OverlaySceneContext(scene, state);
    addOverlayMaterials(sceneContext);

    return sceneContext;
}

const DIRECTIONAL_LIGHT_COLOR = '#ffffff';
const HEMI_LIGHT_SKY = '#ffffff';
const HEMI_LIGHT_GROUND = '#777a70';
const HEMI_LIGHT_POSITION = lpsPoint({ superior: 2000 });
type StandardLights = Readonly<{
    directional?: DirectionalLight;
    hemisphere: HemisphereLight;
}>;

function addStandardLights(scene: Scene): StandardLights {
    return {
        directional: addObject(scene, () => {
            const light = new DirectionalLight(DIRECTIONAL_LIGHT_COLOR);
            light.intensity = 1;
            light.position.set(0, 0, 1000);
            light.updateMatrix();
            return light;
        }),
        hemisphere: addObject(scene, () => {
            const light = new HemisphereLight(HEMI_LIGHT_SKY, HEMI_LIGHT_GROUND, 5);
            light.position.copy(HEMI_LIGHT_POSITION);
            light.updateMatrix();
            return light;
        }),
    };
}
